import React, { useState, useEffect } from 'react';
import { motion, useScroll } from 'framer-motion';
import { Sun, Moon, Globe } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useTranslation } from 'react-i18next';

const Header: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const { scrollY } = useScroll();
    const { theme, toggleTheme } = useTheme();
    const { t, i18n } = useTranslation();

    const isJapanese = i18n.language?.startsWith('ja');

    const toggleLanguage = () => {
        // navigator.language can be "ja-JP" or "en-US", so compare by prefix.
        i18n.changeLanguage(isJapanese ? 'en' : 'ja');
    };

    useEffect(() => {
        return scrollY.onChange((latest) => {
            setIsScrolled(latest > 50);
        });
    }, [scrollY]);

    return (
        <motion.header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
                    ? 'bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50 py-4 shadow-sm'
                    : 'bg-transparent py-6'
                }`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        >
            <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- same-page fragment to the page top */}
                <a href="#top" className="font-bold text-2xl tracking-tighter text-gray-900 dark:text-white cursor-pointer hover:opacity-80 transition-opacity">
                    LinAppWorks
                </a>

                <nav className="flex items-center gap-6 md:gap-8">
                    <div className="hidden md:flex gap-8 items-center">
                        <a href="#apps" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors">
                            {t('header.products')}
                        </a>
                        <a href="#about" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors">
                            {t('header.expertise')}
                        </a>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggleLanguage}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-300 flex items-center gap-1"
                            aria-label="Switch Language"
                        >
                            <Globe size={20} />
                            <span className="text-xs font-bold uppercase">{isJapanese ? 'JP' : 'EN'}</span>
                        </button>

                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-300"
                            aria-label="Toggle Dark Mode"
                        >
                            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                        </button>

                        <a
                            href="mailto:linappworks@gmail.com"
                            className="hidden sm:block px-5 py-2.5 rounded-full bg-black dark:bg-white text-white dark:text-black text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-all hover:scale-105 active:scale-95"
                        >
                            {t('header.contact')}
                        </a>
                    </div>
                </nav>
            </div>
        </motion.header>
    );
};

export default Header;
